import { useEffect, useState } from 'react';
import { formatCurrency } from 'utils/utils';
import { Button } from '../Button';
import Loader from 'components/Loader';
import RadioButton from '../RadioButton';
import TextBox from '../TextBox';
import ShoppingCartService from '../services/shopping-cart.service';
import { Headings } from '../Headings';
import { useErrorBoundary } from 'basket/ErrorBoundary';
import { SupplyOwnFabric } from '../SupplyOwnFabric';
import ProductQtyInput from 'components/ProductQtyInput';
import { OtherFabricSelector } from '../OtherFabricSelector';
import InformationOverlay from "../InformationOverlay";
import useInfoIcons from 'hooks/useInfoIcons';
import UnitConverter from 'components/UnitConverter';
import NumberInput from '../NumberInput';
import Checkbox from '../Checkbox';

export function BasketEditValancesItem(props) {
    const { item, updateFabricItem, close } = props;
    const handleError = useErrorBoundary();
    const [price, setPrice] = useState(item.price);
    const [metres, setMetres] = useState(item.fabric.fabricMetres.toFixed(1) + 'm');
    const convertToBoolean = (value) => value == "1" || value == 1 ? true : (value == "0" || value == 0 ? false : value);
    const [fabricData, setFabricData] = useState({
        headingId: item.fabric.headingId,
        width: item.fabric.width,
        eyeDrop: item.fabric.eyeDrop,
        eyeletId: item.fabric.eyeletId,
        headingHeight: item.fabric.headingHeight,
        liningId: item.fabric.liningId,
        liningColour: item.fabric.liningColour,
        interlining: item.fabric.interlining,
        handSewn: item.fabric.handSewn,
        customisationRequests: item.fabric.customisationRequests,
        windowLocation: item.fabric.windowLocation,
        fabricId: item.fabricId,
        quantity: item.quantity,
        supplyOwnFabricNameOrDescription: item.supplyOwnFabricNameOrDescription,
        FabricRollWidth: item.supplyOwnFabricRollWidth,
        VerticalPatternRepeat: item.supplyOwnFabricPatternRepeat
    });

    const [measurementsChecked, setMeasurementsChecked] = useState(false)
    const [showMeasurementsCheckError, setShowMeasurementsCheckError] = useState(false)

    const [loading, setLoading] = useState(false);

    const [itemTitle, setItemTitle] = useState(`${item.fabric.fabricName} in ${item.colour || ''} Fabric by ${item.fabric.supplier.name || ''}`);
    const title = () => item.supplyOwnFabric ? 'Edit Valances in Supply Your Own Fabric' : `Edit Valances - ${itemTitle}`;

    const [headingsSmallView, setHeadingsSmallView] = useState([]);
    const [headingsAll, setHeadingsAll] = useState([]);
    const [linings, setLinings] = useState([]);
    const [eyelets, setEyelets] = useState([]);

    const [headingHeightMin, setHeadingHeightMin] = useState(null);
    const [headingHeightMax, setHeadingHeightMax] = useState(null);

    const [errors, setErrors] = useState({});
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);

    const [fabrics, setFabrics] = useState([])
    const [totalFabricsCount, setTotalFabricsCount] = useState(0)
    const [selectedFabric, setSelectedFabric] = useState(fabricData.fabricId)

    const infoIcons = useInfoIcons(item.productType);

    const fetchOptions = async () => {
        const optionsResponse = await ShoppingCartService.create('options/8', {})
            .catch(_ => { handleError('Could not load item details. Please contact administrator'); });

        if (optionsResponse) {
            const { headingsAll, linings, eyelets } = optionsResponse;
            setHeadingsAll(headingsAll);
            setLinings(linings);
            setEyelets(eyelets);
        }

        const { headingsSmallView, headingHeightLimitations } = await ShoppingCartService.create('options/1', {
            option: "headingsSmallView",
            headingId: fabricData.headingId
        }).catch(_ => { handleError('Could not load item details. Please contact administrator'); });

        if (headingsSmallView) {
            setHeadingsSmallView(headingsSmallView);
        }
        if (headingHeightLimitations) {
            setHeadingHeightMin(headingHeightLimitations.headingHeightMin);
            setHeadingHeightMax(headingHeightLimitations.headingHeightMax);
        }
        else {
            setHeadingHeightMin(null);
            setHeadingHeightMax(null);
        }
    };

    const handleQuantityChange = (value) => handleInputChange({ target: { name: 'quantity', value: value } })

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'interlining') {
            setFabricData((prev) => ({
                ...prev,
                interlining: value,
                handSewn: value === '1' ? true : prev.handSewn,
            }))

            return;
        }

        if (name === 'liningId') {
            setFabricData((prev) => ({
                ...prev,
                liningId: value,
                interlining: 0,
                handSewn: 0
            }))

            return;
        }

        const updatedData = { ...fabricData, [name]: value };
        setFabricData(updatedData);
    };

    const setHeadingId = (headingId) => {
        const updatedData = { ...fabricData, headingId: headingId };
        setFabricData(updatedData);
    };

    useEffect(() => {
        const hasErrors = Object.values({ ...errors }).some((error) => error);
        setSubmitDisabled(hasErrors);
    }, [errors]);

    const createItem = () => {
        return {
            ...item,
            quantity: fabricData.quantity,
            fabricId: selectedFabric,
            fabric: {
                ...fabricData,
                liningColour: convertToBoolean(fabricData.liningColour),
                interlining: convertToBoolean(fabricData.interlining),
                handSewn: convertToBoolean(fabricData.handSewn),
                liningId: parseInt(fabricData.liningId),
            },
            supplyOwnFabricNameOrDescription: fabricData.supplyOwnFabricNameOrDescription,
            supplyOwnFabricPatternRepeat: fabricData.VerticalPatternRepeat,
            supplyOwnFabricRollWidth: fabricData.FabricRollWidth
        }
    }

    const getConvertedFabricData = (data) => {
        return {
            ...data,
            unitOfMeasurement: convertToBoolean(data.unitOfMeasurement),
            liningColour: convertToBoolean(data.liningColour),
            interlining: convertToBoolean(data.interlining),
            handSewn: convertToBoolean(data.handSewn),
            liningId: parseInt(data.liningId),
        };
    };

    const handlerMeasurementCheck = () => {
        setMeasurementsChecked(!measurementsChecked)
        setShowMeasurementsCheckError(false)
    }

    const handleUpdate = () => {
        if (!measurementsChecked) {
            setShowMeasurementsCheckError(true)
            return
        }

        setLoading(true);
        updateFabricItem(createItem());
        setLoading(false);
    };

    const updatePrice = async () => {
        const item = createItem()

        setErrors([])

        if (item.supplyOwnFabric) {
            const isValidResponse = await fetch('/api/product/supply-own-fabric/validate', {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify({
                    Description: item.supplyOwnFabricNameOrDescription,
                    VerticalPatternRepeat: Number(item.supplyOwnFabricPatternRepeat),
                    FabricRollWidth: Number(item.supplyOwnFabricRollWidth)
                })
            })

            if (isValidResponse.status !== 200) {
                const json = await isValidResponse.json();

                const lowercasedErrors = Object.entries(json).reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

                setErrors(prev => ({
                    ...prev,
                    ...lowercasedErrors
                }));
            }
        }

        const response = await ShoppingCartService.create('price', createItem())
            .catch(_ => { handleError('Could not load item price. Please contact administrator'); });

        if (response) {
            const result = await ShoppingCartService.create('price', createItem())
                .catch(_ => { handleError('Could not load item price. Please contact administrator'); });
            if (result) {
                setPrice(result.price);
                setMetres(result.fabricMetres.toFixed(1) + 'm');
            }
        }
    }

    const [isHookVisible, setIsHookVisible] = useState(false);
    useEffect(() => {
        let hideIds = [4, 5, 8, 12, 13, 14, 15, 22];
        setIsHookVisible(!hideIds.includes(fabricData.headingId));
    }, [fabricData.headingId]);

    const [isEyeletsVisible, setIsEyeletsVisible] = useState(false);
    useEffect(() => {
        let hideIds = [4, 12, 13, 14];
        setIsEyeletsVisible(hideIds.includes(fabricData.headingId));
    }, [fabricData.headingId]);

    const [eyeletSize, setEyeletSize] = useState("");
    const setEyeletId = (eyeletId) => {
        const updatedData = { ...fabricData, eyeletId: eyeletId };
        setFabricData(updatedData);
    };

    const loadFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId)
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const loadAllFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId + '&showAll=true')
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const validatePrice = async (item, callback) => {
        fetch('/api/product/valances/validate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(item)
        })
            .then(response => {
                setErrors([]) // clear errors
                if (!response.ok) {
                    return response.json().then(err => { throw err });
                }
                return response.json();
            })
            .then(() => {
                callback(item);
            })
            .catch(jsonResponse => {
                const lowercasedErrors = Object.entries(jsonResponse).reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

                setErrors(prevErrors => ({
                    ...prevErrors,
                    ...lowercasedErrors
                }));
            });
    }

    useEffect(() => {
        validatePrice(getConvertedFabricData(fabricData), updatePrice)
    }, [fabricData.headingId,
        fabricData.eyeletId,
        fabricData.liningId,
        fabricData.liningColour,
        fabricData.interlining,
        fabricData.handSewn,
        fabricData.fabricId,
        fabricData.quantity,
    ]);

    useEffect(() => setSubmitDisabled(Object.keys(errors).length > 0), [errors])

    useEffect(() => {
        const foundFabric = fabrics.find(fabric => fabric.fabricId === selectedFabric);
        if (foundFabric) {
            setItemTitle(foundFabric.fullName);
            validatePrice(fabricData, updatePrice)
        }
    }, [selectedFabric]);

    useEffect(() => {
        fetchOptions()
        loadFabrics()
    }, []);

    return (
        <Loader isLoading={loading}>
            <form className="p-32 pb-54 bg3 measurements-change active-group sm-p-20">
                <div className="row">
                    <div className="col">
                        <div className="subtitle1 mb-16 sm-text-24 default-text">{title() + ' '}
                            {infoIcons && infoIcons.length > 0 && (
                                <span className="paragraph-info-icon-wrapper">
                                    <InformationOverlay item={infoIcons.find(o => o.name === "Description")} />
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-auto ps-36">
                        <span
                            className="curtains-icon curtains-icon-close size-24 show-btn hide-btn"
                            onClick={() => close()}
                        ></span>
                    </div>
                </div>

                {item.supplyOwnFabric && <SupplyOwnFabric fabricData={fabricData} errors={errors} handleInputChange={handleInputChange} onBlur={() => validatePrice(fabricData, updatePrice)} />}
                {!item.supplyOwnFabric && <OtherFabricSelector fabrics={fabrics} totalFabricsCount={totalFabricsCount} loadAllFabrics={loadAllFabrics} selectedFabric={selectedFabric} setSelectedFabric={setSelectedFabric} />}

                <Headings
                    headingsSmallView={headingsSmallView}
                    headingsAll={headingsAll}
                    headingId={fabricData.headingId}
                    setHeadingId={setHeadingId}
                    setEyeletSize={setEyeletSize}
                    price={price * fabricData.quantity}
                    setHeadingHeightMin={setHeadingHeightMin}
                    setHeadingHeightMax={setHeadingHeightMax}
                    infoIcon={infoIcons.find(o => o.name === "Heading")}
                />

                {/* Measurements */}
                <div>
                    <div className='row'>
                        <div className="col">
                            <div className="body-text3 gray mb-8">Measurements:</div>
                        </div>
                        <div className='col-auto'>
                            {infoIcons && infoIcons.length > 0 && (
                                <InformationOverlay item={infoIcons.find(o => o.name === "HowToMeasure")} clickTitle="How to Measure?" />
                            )}
                        </div>
                    </div>

                    <div className="row d-flex gap-40 sm-gap-16 mb-16">
                        <div className="col">
                            <div className="body-text3 mb-8">Width</div>
                            <NumberInput
                                name="width"
                                value={fabricData.width}
                                onChange={handleInputChange}
                                decimals={1}
                                onBlur={() => validatePrice(fabricData, updatePrice)}
                            />
                            {errors.Width && (
                                <span className="field-validation-error">{errors.Width}</span>
                            )}
                        </div>
                        <div className="col">
                            <div className="body-text3 mb-8">Drop</div>
                            <NumberInput
                                name="eyeDrop"
                                value={fabricData.eyeDrop}
                                onChange={handleInputChange}
                                decimals={1}
                                onBlur={() => validatePrice(fabricData, updatePrice)}
                            />
                            {errors.EyeDrop && (
                                <span className="field-validation-error">{errors.EyeDrop}</span>
                            )}
                        </div>
                    </div>

                    <UnitConverter />
                </div>
                <hr className="secondary-line mb-16"></hr>

                {/* Hook Position */}
                {isHookVisible && (
                    <div className='row'>
                        <div className="row body-text3 gray mt-16 mb-8 align-center gap_3">Hook Position:</div>
                        <div className="col">
                            <div className="body-text3 mb-8">Distance</div>
                            <NumberInput
                                name="headingHeight"
                                value={fabricData.headingHeight}
                                onChange={handleInputChange}
                                decimals={1}
                                onBlur={() => validatePrice(fabricData, updatePrice)}
                            />
                            {errors.HeadingHeight && (
                                <span className="field-validation-error">{errors.HeadingHeight}</span>
                            )}
                        </div>
                        <div className="col-md-6"></div>
                        <div className="dashed-line mt-16"></div>
                    </div>
                )}

                {/* Eyelets */}
                {isEyeletsVisible && (
                    <div>
                        <div className="body-text3 gray mt-16 mb-16 align-center gap_3">Select Eyelet Color ({eyeletSize}):
                            {infoIcons && infoIcons.length > 0 && (
                                <InformationOverlay item={infoIcons.find(o => o.name === "Eyelets")} />
                            )}
                        </div>
                        <div className="thumbnails thumbnails-5 have-first-el-active mb-16 active-group row-gap-16">
                            {eyelets && eyelets.length > 0 && eyelets.map((eyelet) => (
                                eyelet && (
                                    <div className={eyelet.id == fabricData.eyeletId ? "active" : ""} key={'eyeletId' + eyelet.id} onClick={() => setEyeletId(eyelet.id)}>
                                        <div className="thumbnail pointer mb_5 rel">
                                            <img src={eyelet.imageUrl} />
                                            <span className="curtains-icon curtains-icon-Check"></span>
                                        </div>
                                        <div className="body-text6 ps_5">{eyelet.name}</div>
                                    </div>
                                )
                            ))}
                        </div>
                        <hr className="secondary-line" />
                    </div>
                )}

                {/* Lining */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Lining:
                        {infoIcons && infoIcons.length > 0 && (
                            <InformationOverlay item={infoIcons.find(o => o.name === "Lining")} />
                        )}
                    </div>
                    <div className="d-flex gap-32 row-gap-16 nowrap-labels mb-16">
                        {linings && linings.length > 0 && linings.map((lining) => (
                            lining && (
                                <RadioButton
                                    key={'liningId' + lining.id}
                                    name="liningId"
                                    label={lining.name}
                                    value={lining.id}
                                    checked={fabricData.liningId == lining.id}
                                    onChange={handleInputChange}
                                />
                            )
                        ))}
                    </div>
                </div>
                <hr className="secondary-line" />

                {/* Lining Colour */}
                {
                    fabricData.liningId != 7 &&
                        <div>
                            <div className="body-text3 gray mt-16 mb-16 align-center gap_3">Lining Colour:
                                {infoIcons && infoIcons.length > 0 && (
                                    <InformationOverlay item={infoIcons.find(o => o.name === "LiningColour")} />
                                )}
                            </div>
                            <div className="d-flex gap-32 mb-16">
                                <RadioButton
                                    name="liningColour"
                                    label={'Ivory'}
                                    value={0}
                                    checked={fabricData.liningColour == 0}
                                    onChange={handleInputChange}
                                />
                                <RadioButton
                                    name="liningColour"
                                    label={'White'}
                                    value={1}
                                    checked={fabricData.liningColour == 1}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <hr className="secondary-line mb-16" />
                        </div>
                }

                {/* Interlining */}
                {
                    fabricData.liningId != 7 &&
                    <div>
                        <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Interlining:
                            {infoIcons && infoIcons.length > 0 && (
                                <InformationOverlay item={infoIcons.find(o => o.name === "Interlining")} />
                            )}
                        </div>
                        <div className="d-flex gap-32 mb-16">
                            <RadioButton
                                name="interlining"
                                label={'Yes'}
                                value={1}
                                checked={fabricData.interlining == 1}
                                onChange={handleInputChange}
                            />
                            <RadioButton
                                name="interlining"
                                label={'No'}
                                value={0}
                                checked={fabricData.interlining == 0}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="dashed-line"></div>
                    </div>
                }

                {/* Handsewn */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Handsewn:
                        {infoIcons && infoIcons.length > 0 && (
                            <InformationOverlay item={infoIcons.find(o => o.name === "Handsewn")} />
                        )}
                    </div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            disabled={fabricData.interlining == 1}
                            name="handSewn"
                            label={'Yes'}
                            value={1}
                            checked={fabricData.handSewn == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            disabled={fabricData.interlining == 1}
                            name="handSewn"
                            label={'No'}
                            value={0}
                            checked={fabricData.handSewn == 0}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="dashed-line"></div>

                {/* Customization */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Curtain Valance Customisation:</div>
                    <div className="max-width-410 mb-16 sm-text-14">If you have special requests, write below or <a href="/contact-us" className="underline">contact us</a> <a href="tel:0345 8620743" className="underline">0345 8620743</a>.</div>
                    <TextBox
                        name="customisationRequests"
                        value={fabricData.customisationRequests}
                        onChange={handleInputChange}
                        placeholder='e.g. Trim or borders'
                        onBlur={() => validatePrice(fabricData, updatePrice)}
                    />
                    {errors.CustomisationRequests && (
                        <span className="field-validation-error">{errors.CustomisationRequests}</span>
                    )}
                </div>
                <div className="dashed-line mt-16"></div>

                {/* Window Location */}
                <div style={{ marginBottom: '16px' }}>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Window Location:</div>
                    <div className="max-width-410 mb-16 sm-text-14">Let us know where you will be hanging your Curtain Valance, and we will include this in your order details.</div>
                    <TextBox
                        name="windowLocation"
                        value={fabricData.windowLocation}
                        onChange={handleInputChange}
                        placeholder='e.g. Living room front window'
                        onBlur={() => validatePrice(fabricData, updatePrice)}
                    />
                    {errors.WindowLocation && (
                        <span className="field-validation-error">{errors.WindowLocation}</span>
                    )}
                </div>

                {
                    item.supplyOwnFabric && 
                        <div>
                            <div className="body-text3 gray mt-32 mb-8 align-center gap_3">Quantity of fabric per item to be supplied by you:</div>
                            <TextBox name='sof-fabric-length' value={metres} className='max-width-150 mb-16' />
                        </div>
                }

                <Checkbox
                    checked={measurementsChecked}
                    label={'I have checked all measurements'}
                    value={!measurementsChecked}
                    onChange={handlerMeasurementCheck}
                    requiredMessage={''}
                    name={'measurementsChecked'} />
                {showMeasurementsCheckError && <div className="text-danger m-8">Please confirm you have checked everything.</div>}

                <div className="dashed-line mt-16"></div>

                {/* TOTAL */}
                <div>
                    <div className="subtitle2 mb-8 mt-16">TOTAL: <span className="body-text1">{formatCurrency(price, 2)}</span></div>
                    <div className="body-text6">Delivery for made-to-measure curtain valances: {item.deliveryContent} weeks.</div>
                </div>

                <div className="mb-16 mt-32">
                    <div className="dashed-line mb-24"></div>
                    <div className="body-text3 gray mb-16">Quantity:</div>

                    <ProductQtyInput
                        digits={0}
                        initValue={fabricData.quantity}
                        onlyInt={true}
                        onChange={handleQuantityChange}
                        onBlur={() => validatePrice(fabricData, updatePrice)}
                    />
                    {errors.Quantity && (
                        <span className="field-validation-error">{errors.Quantity}</span>
                    )}
                </div>

                <div className="d-flex gap-16 mb-16 sm-column md-column sm-gap-8 sm-mb-0">
                    <div className="w-100 show-btn hide-btn">
                        <Button name="UPDATE" onClick={handleUpdate} disabled={isSubmitDisabled || Object.keys(errors).length > 0} />
                    </div>
                </div>
            </form>
        </Loader>
    );
}